import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, Users, Phone, MessageSquare, ShoppingBag, MapPin, Loader2, Bell, BellOff, ExternalLink, X, Mail, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import InstallPWAButton from '../components/InstallPWAButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUS = [
  { id: 'pending', label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' },
  { id: 'confirmed', label: 'Confirmée', color: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  { id: 'preparing', label: 'En préparation', color: 'bg-purple-500/20 text-purple-400 border-purple-500/40' },
  { id: 'delivering', label: 'En livraison', color: 'bg-orange-500/20 text-orange-400 border-orange-500/40' },
  { id: 'delivered', label: 'Livrée', color: 'bg-green-500/20 text-green-400 border-green-500/40' },
  { id: 'cancelled', label: 'Annulée', color: 'bg-red-500/20 text-red-400 border-red-500/40' },
];
const ORDER_STATUS_MAP = ORDER_STATUS.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr.length === 10 ? dateStr + 'T00:00:00' : dateStr);
  const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
};

// Local date utilities (avoid UTC drift)
const FR_MONTHS_LONG = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const FR_DAYS_SHORT = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

const toLocalISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseISO = (iso) => new Date(iso + 'T00:00:00');

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

// Monday of the week containing `date` (week starts Monday)
const startOfWeek = (date) => {
  const d = new Date(date);
  const dow = d.getDay(); // 0 Sun .. 6 Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  return addDays(d, diff);
};


const formatTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const PainelUnificado = () => {
  const [reservations, setReservations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [filter, setFilter] = useState('active'); // active, today, all
  const [activeTab, setActiveTab] = useState('reservations'); // 'reservations' | 'orders'
  const [soundOn, setSoundOn] = useState(true);
  const [lastCounts, setLastCounts] = useState({ res: 0, ord: 0 });
  const [selectedDetail, setSelectedDetail] = useState(null); // {type:'reservation'|'order', data: {...}}
  const [okReservations, setOkReservations] = useState(() => {
    try {
      const raw = localStorage.getItem('beni_panel_ok_reservations');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const [selectedDate, setSelectedDate] = useState(() => toLocalISO(new Date())); // 'YYYY-MM-DD'
  const [terraceEnabled, setTerraceEnabled] = useState(true);

  // Fetch terrace state on mount and refresh with fetchAll
  useEffect(() => {
    const loadTerrace = () => {
      axios.get(`${API}/settings/public`)
        .then((r) => setTerraceEnabled(r.data?.terrace_enabled ?? true))
        .catch(() => {});
    };
    loadTerrace();
    const id = setInterval(loadTerrace, 30000);
    return () => clearInterval(id);
  }, []);

  const toggleTerrace = async () => {
    const next = !terraceEnabled;
    if (!next && !window.confirm("Désactiver la terrasse ? Les nouvelles réservations en terrasse seront refusées.")) return;
    try {
      await axios.put(`${API}/settings/terrace/public?enabled=${next}`);
      setTerraceEnabled(next);
    } catch (e) {
      alert("Erreur. Veuillez réessayer.");
    }
  };

  const toggleOk = useCallback((id) => {
    setOkReservations(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem('beni_panel_ok_reservations', JSON.stringify([...next])); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [resR, resO] = await Promise.all([
        axios.get(`${API}/reservations/display`),
        axios.get(`${API}/delivery/orders/display`)
      ]);
      const newRes = resR.data || [];
      const newOrd = resO.data || [];
      
      // Sound alert on new items (compare to previous state via setter callback)
      setLastCounts(prev => {
        if (soundOn && (newRes.length > prev.res || newOrd.length > prev.ord) && prev.res + prev.ord > 0) {
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain); gain.connect(ctx.destination);
            osc.frequency.value = 880; osc.type = 'sine';
            gain.gain.setValueAtTime(0.15, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
            osc.start(); osc.stop(ctx.currentTime + 0.6);
          } catch (e) { /* ignore */ }
        }
        return { res: newRes.length, ord: newOrd.length };
      });
      setReservations(newRes);
      setOrders(newOrd);
    } catch (e) {
      console.error('Fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [soundOn]);

  useEffect(() => {
    const interval = setInterval(fetchAll, 15000);
    const t = setTimeout(fetchAll, 0);
    return () => { clearInterval(interval); clearTimeout(t); };
  }, [fetchAll]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/delivery/orders/${orderId}/status/public?status=${newStatus}`);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (e) { console.error(e); }
  };

  const today = toLocalISO(now);
  const todayISO = today;

  // Filter reservations
  const filteredRes = reservations.filter(r => {
    if (filter === 'today') return r.date === today;
    if (filter === 'active') return r.date >= today && r.status !== 'cancelled';
    return true;
  });

  // Filter orders
  const filteredOrders = orders.filter(o => {
    if (filter === 'today') return o.created_at?.split('T')[0] === todayISO;
    if (filter === 'active') return !['delivered', 'cancelled'].includes(o.status);
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-3 md:p-5" data-testid="painel-unificado">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#d4af37] rounded flex items-center justify-center">
              <span className="text-black font-display text-lg">B</span>
            </div>
            <div>
              <h1 className="text-lg md:text-2xl text-white font-semibold tracking-wide">BÉNI — Tableau de Bord</h1>
              <p className="text-white/40 text-[11px] mt-0.5">
                Mis à jour : {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })} · Actualisation auto 15s
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <InstallPWAButton label="Installer dans Chrome" />
            <button
              onClick={toggleTerrace}
              data-testid="painel-terrace-toggle"
              title={terraceEnabled ? "Terrasse activée — cliquez pour fermer" : "Terrasse fermée — cliquez pour rouvrir"}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition active:scale-95 ${
                terraceEnabled
                  ? 'bg-sky-400/10 text-sky-300 border-sky-400/40 hover:bg-sky-400/20'
                  : 'bg-red-500/15 text-red-300 border-red-500/40 hover:bg-red-500/25'
              }`}
            >
              <span className="text-base leading-none">🌿</span>
              <span>Terrasse</span>
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${terraceEnabled ? 'bg-sky-400/20 text-sky-200' : 'bg-red-500/25 text-red-200'}`}>
                {terraceEnabled ? 'Ouverte' : 'Fermée'}
              </span>
            </button>
            <button onClick={() => setSoundOn(!soundOn)} className="text-white/50 hover:text-[#d4af37] p-1.5 transition" title={soundOn ? 'Désactiver son' : 'Activer son'} data-testid="painel-sound-toggle">
              {soundOn ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            </button>
            <button onClick={fetchAll} className="text-[#d4af37] text-xs border border-[#d4af37]/30 px-3 py-1.5 rounded hover:bg-[#d4af37]/10 transition" data-testid="painel-refresh">
              Actualiser
            </button>
          </div>
        </div>

        {/* Tabs + Filters */}
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex gap-1 bg-[#0d0d0d] border border-white/10 rounded-lg p-1" data-testid="painel-tabs">
            <button
              onClick={() => setActiveTab('reservations')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
                activeTab === 'reservations'
                  ? 'bg-[#d4af37] text-black shadow'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
              data-testid="tab-reservations"
            >
              <Calendar className="w-4 h-4" />
              <span>Réservations</span>
              <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${activeTab === 'reservations' ? 'bg-black/15 text-black' : 'bg-white/10 text-white/70'}`}>
                {filteredRes.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
                activeTab === 'orders'
                  ? 'bg-[#d4af37] text-black shadow'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
              data-testid="tab-orders"
            >
              <ShoppingBag className="w-4 h-4" />
              <span>Commandes Livraison</span>
              <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${activeTab === 'orders' ? 'bg-black/15 text-black' : 'bg-white/10 text-white/70'}`}>
                {filteredOrders.length}
              </span>
            </button>
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {[
              { id: 'active', label: 'En cours' },
              { id: 'today', label: "Aujourd'hui" },
              { id: 'all', label: 'Tout' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 text-xs rounded transition ${
                  filter === f.id ? 'bg-[#d4af37] text-black font-medium' : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
                data-testid={`painel-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active tab content */}
        <div className="bg-[#0d0d0d] border-2 border-white/10 rounded-lg overflow-hidden">
          <div className="p-2 md:p-3 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
            {activeTab === 'reservations' && (
              <ReservationsWeekView
                reservations={filteredRes}
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
                today={today}
                okSet={okReservations}
                onToggleOk={toggleOk}
                onCardClick={(r) => setSelectedDetail({ type: 'reservation', data: r })}
              />
            )}
            {activeTab === 'orders' && (
              filteredOrders.length === 0 ? (
                <EmptyState icon={<ShoppingBag className="w-10 h-10" />} label="Aucune commande" />
              ) : (
                <AnimatePresence>
                  {filteredOrders.map((o, idx) => (
                    <OrderCard key={o.id} o={o} idx={idx} onStatusChange={updateOrderStatus} onClick={() => setSelectedDetail({ type: 'order', data: o })} />
                  ))}
                </AnimatePresence>
              )
            )}
          </div>
        </div>

        {/* Footer tip */}
        <div className="mt-6 text-center text-white/30 text-[10px]">
          💡 Astuce : ajoutez cette page à l&apos;écran d&apos;accueil de votre tablette pour un mode kiosque.
        </div>

        {/* Detail Modal */}
        <AnimatePresence>
          {selectedDetail && (
            <DetailModal
              detail={selectedDetail}
              onClose={() => setSelectedDetail(null)}
              onStatusChange={(orderId, newStatus) => {
                updateOrderStatus(orderId, newStatus);
                setSelectedDetail(prev => prev && prev.type === 'order' ? { ...prev, data: { ...prev.data, status: newStatus } } : prev);
              }}
              onCancelReservation={async (reservationId) => {
                try {
                  await axios.put(`${API}/reservations/${reservationId}/cancel/public`);
                  setReservations(prev => prev.map(r => r.id === reservationId ? { ...r, status: 'cancelled' } : r));
                  setSelectedDetail(prev => prev && prev.type === 'reservation' ? { ...prev, data: { ...prev.data, status: 'cancelled' } } : prev);
                } catch (e) {
                  console.error('Cancel reservation failed', e);
                  alert("Erreur lors de l'annulation. Veuillez réessayer.");
                }
              }}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ============== SUB COMPONENTS ==============

const EmptyState = ({ icon, label }) => (
  <div className="text-center py-10 text-white/20">
    <div className="flex justify-center mb-2 opacity-50">{icon}</div>
    <p className="text-white/30 text-sm">{label}</p>
  </div>
);

/**
 * ReservationsWeekView — month label + week strip + reservations grouped by day.
 * Props:
 *   reservations: all reservations (already filtered by status preference)
 *   selectedDate: 'YYYY-MM-DD' currently focused day
 *   onSelectDate: (iso) => void
 *   today: 'YYYY-MM-DD'
 *   okSet: Set<string> of OK-toggled reservation ids
 *   onToggleOk: (id) => void
 *   onCardClick: (reservation) => void
 */
const ReservationsWeekView = ({ reservations, selectedDate, onSelectDate, today, okSet, onToggleOk, onCardClick }) => {
  const selDate = parseISO(selectedDate);
  const weekStart = startOfWeek(selDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekStartISO = toLocalISO(weekStart);
  const weekEndISO = toLocalISO(addDays(weekStart, 6));

  // Reservations in this week (any status except cancelled — those are not visually counted but still listed)
  const weekReservations = reservations
    .filter((r) => r.date >= weekStartISO && r.date <= weekEndISO)
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  // Count active (non-cancelled) reservations per ISO date — used for the dot indicator
  const countByDate = weekReservations.reduce((acc, r) => {
    if (r.status === 'cancelled') return acc;
    acc[r.date] = (acc[r.date] || 0) + 1;
    return acc;
  }, {});

  // Group reservations by date for the list
  const groupedByDate = weekReservations.reduce((acc, r) => {
    (acc[r.date] = acc[r.date] || []).push(r);
    return acc;
  }, {});
  const orderedDates = Object.keys(groupedByDate).sort();

  // Month label (use the week start month; if week crosses months, show "juin / juillet")
  const monthLabel = (() => {
    const a = weekStart.getMonth();
    const b = addDays(weekStart, 6).getMonth();
    if (a === b) return `${FR_MONTHS_LONG[a]} ${weekStart.getFullYear()}`;
    return `${FR_MONTHS_LONG[a]} / ${FR_MONTHS_LONG[b]} ${addDays(weekStart, 6).getFullYear()}`;
  })();

  const goPrevWeek = () => onSelectDate(toLocalISO(addDays(selDate, -7)));
  const goNextWeek = () => onSelectDate(toLocalISO(addDays(selDate, 7)));
  const goToday = () => onSelectDate(today);

  return (
    <div className="space-y-3" data-testid="reservations-week-view">
      {/* Month / Week navigation */}
      <div className="flex items-center justify-between gap-2 px-1">
        <button
          onClick={goPrevWeek}
          className="text-white/60 hover:text-[#d4af37] p-1.5 rounded transition active:scale-90"
          title="Semaine précédente"
          data-testid="week-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <span className="text-white text-sm md:text-base capitalize tracking-wide">{monthLabel}</span>
          {selectedDate !== today && (
            <button
              onClick={goToday}
              className="text-[10px] uppercase tracking-wider text-[#d4af37] border border-[#d4af37]/40 px-2 py-0.5 rounded hover:bg-[#d4af37]/10 transition"
              data-testid="week-today"
            >
              Aujourd&apos;hui
            </button>
          )}
        </div>
        <button
          onClick={goNextWeek}
          className="text-white/60 hover:text-[#d4af37] p-1.5 rounded transition active:scale-90"
          title="Semaine suivante"
          data-testid="week-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Week strip — 7 day pills */}
      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((d) => {
          const iso = toLocalISO(d);
          const isSel = iso === selectedDate;
          const isToday = iso === today;
          const count = countByDate[iso] || 0;
          return (
            <button
              key={iso}
              onClick={() => onSelectDate(iso)}
              data-testid={`week-day-${iso}`}
              className={`flex flex-col items-center gap-1 py-2 rounded-lg transition active:scale-95 ${
                isSel
                  ? 'bg-[#d4af37] text-black shadow-lg shadow-[#d4af37]/20'
                  : isToday
                    ? 'bg-[#d4af37]/10 text-[#d4af37] hover:bg-[#d4af37]/20'
                    : 'bg-white/5 text-white/70 hover:bg-white/10'
              }`}
            >
              <span className={`text-[10px] uppercase tracking-wider ${isSel ? 'text-black/70' : 'text-current opacity-60'}`}>
                {FR_DAYS_SHORT[d.getDay()]}
              </span>
              <span className={`text-base md:text-lg font-bold leading-none ${isSel ? 'text-black' : 'text-current'}`}>
                {d.getDate()}
              </span>
              <span className="h-1.5 flex items-center justify-center">
                {count > 0 && (
                  <span
                    className={`inline-block rounded-full ${
                      isSel ? 'bg-black/70' : 'bg-[#d4af37]'
                    }`}
                    style={{ width: 5, height: 5 }}
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Reservations list grouped by day */}
      {orderedDates.length === 0 ? (
        <EmptyState icon={<Calendar className="w-10 h-10" />} label="Aucune réservation cette semaine" />
      ) : (
        <div className="space-y-3">
          {orderedDates.map((iso) => {
            const items = groupedByDate[iso];
            const d = parseISO(iso);
            const active = items.filter((r) => r.status !== 'cancelled');
            const totalGuests = active.reduce((s, r) => s + (r.guests || 0), 0);
            const isSel = iso === selectedDate;
            return (
              <div key={iso} className="space-y-1.5">
                <button
                  onClick={() => onSelectDate(iso)}
                  className={`w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded transition ${
                    isSel ? 'bg-[#d4af37]/15 text-[#d4af37]' : 'bg-white/[0.03] text-white/60 hover:text-white'
                  }`}
                  data-testid={`day-header-${iso}`}
                >
                  <span className="text-[11px] uppercase tracking-wider font-medium">
                    {FR_DAYS_SHORT[d.getDay()]}, {d.getDate()} {FR_MONTHS_LONG[d.getMonth()].slice(0, 4)}
                  </span>
                  <span className="text-[10px] flex items-center gap-3 opacity-80">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {active.length}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" /> {totalGuests}
                    </span>
                  </span>
                </button>
                <AnimatePresence>
                  {items.map((r, idx) => (
                    <ReservationCard
                      key={r.id}
                      r={r}
                      today={today}
                      idx={idx}
                      isOk={okSet.has(r.id)}
                      onToggleOk={() => onToggleOk(r.id)}
                      onClick={() => onCardClick(r)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ReservationCard = ({ r, today, idx, isOk, onToggleOk, onClick }) => {
  const isToday = r.date === today;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.02 }}
      onClick={onClick}
      className={`bg-[#121212] border rounded p-2.5 flex items-center gap-3 cursor-pointer hover:bg-[#1a1a1a] transition active:scale-[0.99] ${
        isOk ? 'border-emerald-500/60 opacity-60' :
        r.status === 'confirmed' ? 'border-green-500/30' :
        r.status === 'cancelled' ? 'border-red-500/20 opacity-50' :
        'border-[#d4af37]/30'
      }`}
      data-testid={`res-card-${r.id}`}
    >
      <div className={`w-1.5 h-12 rounded shrink-0 ${
        isOk ? 'bg-emerald-500' :
        r.status === 'confirmed' ? 'bg-green-500' :
        r.status === 'cancelled' ? 'bg-red-500' :
        'bg-[#d4af37] animate-pulse'
      }`} />
      <div className="shrink-0 w-14 text-center">
        <p className={`text-[10px] font-medium ${isToday ? 'text-[#d4af37]' : 'text-white/60'}`}>
          {isToday ? "Auj." : formatDate(r.date)}
        </p>
        <p className="text-white text-base font-bold leading-tight">{r.time}</p>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`font-medium text-sm truncate ${isOk ? 'text-white/60 line-through' : 'text-white'}`}>{r.name}</p>
          {r.seating === 'terrace' && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-400/15 text-sky-300 border border-sky-400/30" data-testid={`res-seating-${r.id}`}>
              🌿 Terrasse
            </span>
          )}
          {r.seating === 'interior' && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-white/50 border border-white/10" data-testid={`res-seating-${r.id}`}>
              Intérieur
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-white/40 text-[11px] mt-0.5 flex-wrap">
          <span className="flex items-center gap-0.5"><Users className="w-3 h-3" />{r.guests} pers.</span>
          <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5 hover:text-[#d4af37]"><Phone className="w-3 h-3" />{r.phone}</a>
        </div>
        {r.notes && (
          <div className="flex items-start gap-1 mt-1 text-yellow-400/70 text-[10px]">
            <MessageSquare className="w-2.5 h-2.5 shrink-0 mt-0.5" />
            <span className="italic truncate">{r.notes}</span>
          </div>
        )}
      </div>
      <div className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-medium ${
        r.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
        r.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
        'bg-[#d4af37]/20 text-[#d4af37]'
      }`}>
        {r.status === 'confirmed' ? 'Confirmé' : r.status === 'cancelled' ? 'Annulé' : 'En attente'}
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleOk(); }}
        title={isOk ? 'Desmarcar OK' : 'Marcar OK'}
        aria-pressed={isOk}
        data-testid={`res-ok-toggle-${r.id}`}
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition active:scale-90 ${
          isOk
            ? 'bg-emerald-500 border-emerald-400 text-black shadow-[0_0_10px_rgba(16,185,129,0.45)]'
            : 'bg-transparent border-white/20 text-white/40 hover:border-emerald-400 hover:text-emerald-400'
        }`}
      >
        <Check className="w-4 h-4" strokeWidth={3} />
      </button>
    </motion.div>
  );
};

const OrderCard = ({ o, idx, onStatusChange, onClick }) => {
  const status = ORDER_STATUS_MAP[o.status] || ORDER_STATUS_MAP.pending;
  const isCancelledOrDone = ['delivered', 'cancelled'].includes(o.status);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.address)}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.02 }}
      onClick={onClick}
      className={`bg-[#121212] border rounded p-2.5 cursor-pointer hover:bg-[#1a1a1a] transition active:scale-[0.99] ${
        o.status === 'pending' ? 'border-yellow-500/40' :
        o.status === 'delivered' ? 'border-green-500/30 opacity-60' :
        o.status === 'cancelled' ? 'border-red-500/20 opacity-50' :
        'border-white/10'
      }`}
      data-testid={`order-card-${o.id}`}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <div className={`w-1.5 h-12 rounded shrink-0 ${
          o.status === 'pending' ? 'bg-yellow-400 animate-pulse' :
          o.status === 'delivering' ? 'bg-orange-400 animate-pulse' :
          o.status === 'delivered' ? 'bg-green-500' :
          o.status === 'cancelled' ? 'bg-red-500' :
          'bg-blue-400'
        }`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-medium text-sm">{o.customer_name}</p>
            <span className="text-[#d4af37] font-bold text-sm">{o.total?.toFixed(2)}€</span>
            <span className="text-white/30 text-[10px]">{formatTime(o.created_at)}</span>
          </div>
          <div className="flex items-center gap-2 text-white/50 text-[11px] mt-0.5 flex-wrap">
            <a href={`tel:${o.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5 hover:text-[#d4af37]"><Phone className="w-3 h-3" />{o.phone}</a>
            <a href={mapsUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5 hover:text-[#d4af37] truncate max-w-[200px]"><MapPin className="w-3 h-3" />{o.address}<ExternalLink className="w-2 h-2 ml-0.5" /></a>
          </div>
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-medium border ${status.color}`}>{status.label}</span>
      </div>
      <div className="pl-3.5 text-white/55 text-[11px] mb-1.5">
        {o.items?.map((it, i) => (
          <div key={i} className="mb-0.5">
            <span><span className="text-[#d4af37]">{it.quantity}×</span> {it.name_fr}</span>
            {it.notes && <span className="block pl-3 text-yellow-400/70 italic text-[10px]">↳ {it.notes}</span>}
          </div>
        ))}
        {o.delivery_fee > 0 && <span className="text-white/30 text-[10px] block mt-0.5">+ livraison {o.delivery_fee.toFixed(2)}€</span>}
      </div>
      {o.notes && (
        <div className="pl-3.5 flex items-start gap-1 mb-1.5 text-yellow-400/70 text-[10px]">
          <MessageSquare className="w-2.5 h-2.5 shrink-0 mt-0.5" />
          <span className="italic">{o.notes}</span>
        </div>
      )}
      {!isCancelledOrDone && (
        <div className="pl-3.5 flex flex-wrap gap-1 pt-1.5 border-t border-white/5">
          {ORDER_STATUS.filter(s => s.id !== o.status).map(s => (
            <button
              key={s.id}
              onClick={(e) => { e.stopPropagation(); onStatusChange(o.id, s.id); }}
              className={`text-[10px] px-1.5 py-0.5 rounded border transition hover:opacity-80 ${s.color}`}
              data-testid={`order-action-${o.id}-${s.id}`}
            >
              → {s.label}
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// ============== DETAIL MODAL ==============

const DetailModal = ({ detail, onClose, onStatusChange, onCancelReservation }) => {
  const { type, data } = detail;
  const isOrder = type === 'order';
  const isReservation = type === 'reservation';
  const mapsUrl = isOrder ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.address)}` : '';
  const subtotal = isOrder ? (data.subtotal || (data.total - (data.delivery_fee || 0))) : 0;
  const status = isOrder ? (ORDER_STATUS_MAP[data.status] || ORDER_STATUS_MAP.pending) : null;
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  // Auto-reset the "confirm cancel" state after 6 seconds of inactivity
  useEffect(() => {
    if (!confirmingCancel) return;
    const id = setTimeout(() => setConfirmingCancel(false), 6000);
    return () => clearTimeout(id);
  }, [confirmingCancel]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      data-testid="detail-modal"
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0d0d0d] border border-white/10 w-full md:max-w-lg md:rounded-lg rounded-t-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className={`sticky top-0 bg-[#0d0d0d] p-4 border-b border-white/10 flex items-center justify-between gap-3 ${isOrder ? 'border-l-4 border-l-[#d4af37]' : 'border-l-4 border-l-blue-400'}`}>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-white/40">
              {isOrder ? '🛵 Commande Livraison' : '📅 Réservation'}
            </p>
            <h2 className="text-white text-lg md:text-xl font-semibold truncate">
              {isOrder ? data.customer_name : data.name}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-2 -mr-2" data-testid="detail-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Status / Key info */}
          {isOrder && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className={`px-3 py-1 rounded text-xs font-medium border ${status.color}`}>{status.label}</span>
              <span className="text-[#d4af37] font-bold text-2xl">{data.total?.toFixed(2)}€</span>
            </div>
          )}
          {isReservation && (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-[#d4af37] text-2xl font-bold">{data.time}</p>
                  <p className="text-white/60 text-sm">{formatDate(data.date)} · {data.guests} {data.guests > 1 ? 'personnes' : 'personne'}</p>
                </div>
                <span className={`px-3 py-1 rounded text-xs font-medium ${
                  data.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                  data.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                  'bg-[#d4af37]/20 text-[#d4af37]'
                }`}>
                  {data.status === 'confirmed' ? 'Confirmée' : data.status === 'cancelled' ? 'Annulée' : 'En attente'}
                </span>
              </div>
              {/* Seating preference */}
              <div className="flex items-center gap-2" data-testid="detail-seating">
                <span className="text-white/40 text-[10px] uppercase tracking-wider">Place</span>
                <span className={`px-2 py-1 rounded text-xs font-medium border ${
                  data.seating === 'terrace'
                    ? 'bg-sky-400/15 text-sky-300 border-sky-400/30'
                    : 'bg-white/5 text-white/70 border-white/10'
                }`}>
                  {data.seating === 'terrace' ? '🌿 Terrasse' : 'Intérieur'}
                </span>
              </div>
            </>
          )}

          {/* Contact */}
          <div className="space-y-2 bg-black/30 p-3 rounded">
            <a href={`tel:${data.phone}`} className="flex items-center gap-2 text-white hover:text-[#d4af37] transition" data-testid="detail-phone-link">
              <Phone className="w-4 h-4 text-[#d4af37] shrink-0" />
              <span className="font-medium">{data.phone}</span>
            </a>
            {(isOrder ? data.customer_email : data.email) && (
              <a href={`mailto:${isOrder ? data.customer_email : data.email}`} className="flex items-center gap-2 text-white/70 hover:text-[#d4af37] transition break-all">
                <Mail className="w-4 h-4 text-[#d4af37] shrink-0" />
                <span>{isOrder ? data.customer_email : data.email}</span>
              </a>
            )}
            {isOrder && (
              <a href={mapsUrl} target="_blank" rel="noreferrer" className="flex items-start gap-2 text-white/70 hover:text-[#d4af37] transition">
                <MapPin className="w-4 h-4 text-[#d4af37] shrink-0 mt-0.5" />
                <span className="flex-1">{data.address}</span>
                <ExternalLink className="w-3 h-3 shrink-0 mt-1" />
              </a>
            )}
          </div>

          {/* Items (only for orders) */}
          {isOrder && data.items?.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-white/60 text-xs uppercase tracking-wider">Commande</h3>
              <div className="bg-black/30 rounded p-3 space-y-2">
                {data.items.map((it, i) => (
                  <div key={i} className="pb-2 border-b border-white/5 last:border-0 last:pb-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-white text-sm flex-1">
                        <span className="text-[#d4af37] font-bold">{it.quantity}×</span> {it.name_fr}
                      </p>
                      <p className="text-white/60 text-sm shrink-0">{(it.price * it.quantity).toFixed(2)}€</p>
                    </div>
                    {it.notes && (
                      <div className="flex items-start gap-1 mt-1 pl-5 text-yellow-400 text-xs">
                        <MessageSquare className="w-3 h-3 shrink-0 mt-0.5" />
                        <span className="italic break-words">{it.notes}</span>
                      </div>
                    )}
                  </div>
                ))}
                {data.delivery_fee > 0 && (
                  <div className="pt-2 border-t border-white/10 flex justify-between text-xs">
                    <span className="text-white/50">Sous-total</span>
                    <span className="text-white/70">{subtotal.toFixed(2)}€</span>
                  </div>
                )}
                {data.delivery_fee > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-white/50">Frais de livraison</span>
                    <span className="text-white/70">{data.delivery_fee.toFixed(2)}€</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Global notes */}
          {data.notes && (
            <div className="space-y-2">
              <h3 className="text-white/60 text-xs uppercase tracking-wider">Note du client</h3>
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded text-yellow-300 text-sm whitespace-pre-wrap break-words" data-testid="detail-customer-notes">
                {data.notes}
              </div>
            </div>
          )}

          {/* Created at */}
          {data.created_at && (
            <p className="text-white/30 text-[10px] text-center pt-2 border-t border-white/5" data-testid="detail-created-at">
              {isOrder ? 'Commande reçue le ' : 'Réservation effectuée le '}
              {new Date(data.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
          )}
        </div>

        {/* Reservation cancel action (with double validation) */}
        {isReservation && data.status !== 'cancelled' && (
          <div className="sticky bottom-0 bg-[#0d0d0d] p-3 border-t border-white/10">
            {!confirmingCancel ? (
              <button
                onClick={() => setConfirmingCancel(true)}
                className="w-full text-sm px-3 py-2.5 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10 transition active:scale-[0.99] flex items-center justify-center gap-2"
                data-testid="detail-cancel-reservation"
              >
                <X className="w-4 h-4" /> Annuler la réservation
              </button>
            ) : (
              <div className="space-y-2" data-testid="detail-cancel-confirm">
                <p className="text-red-300 text-xs text-center">
                  Êtes-vous sûr ? Le client recevra un e-mail d&apos;annulation, et l&apos;e-mail de feedback ne sera pas envoyé.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmingCancel(false)}
                    className="flex-1 text-sm px-3 py-2.5 rounded border border-white/20 text-white/70 hover:bg-white/5 transition"
                    data-testid="detail-cancel-abort"
                  >
                    Non, garder
                  </button>
                  <button
                    onClick={() => { onCancelReservation(data.id); setConfirmingCancel(false); }}
                    className="flex-1 text-sm px-3 py-2.5 rounded bg-red-600 text-white hover:bg-red-500 transition font-medium"
                    data-testid="detail-cancel-confirm-button"
                  >
                    Oui, annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Order status actions */}
        {isOrder && !['delivered', 'cancelled'].includes(data.status) && (
          <div className="sticky bottom-0 bg-[#0d0d0d] p-3 border-t border-white/10 flex flex-wrap gap-2">
            {ORDER_STATUS.filter(s => s.id !== data.status).map(s => (
              <button
                key={s.id}
                onClick={() => onStatusChange(data.id, s.id)}
                className={`text-xs px-3 py-2 rounded border transition hover:opacity-80 flex-1 min-w-[110px] ${s.color}`}
                data-testid={`detail-action-${s.id}`}
              >
                → {s.label}
              </button>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PainelUnificado;
